import { useMemo, useState } from "react";
import { Background, Controls, Edge, MarkerType, MiniMap, Node, ReactFlow } from "@xyflow/react";

import { GraphEdge, GraphNode, GraphPayload, GraphSourceOrigin } from "../../api";
import styles from "./EvidenceGraph.module.css";
import { clinicalLabel, getNodeProperty, safeEdgeLabel, SOURCE_COLORS, sourceOriginLabel } from "./graphPresentation";

interface RendererProps {
    payload: GraphPayload;
    onCitationClicked?: (citationFilePath: string) => void;
}

export function GraphExplorerRenderer({ payload, onCitationClicked }: RendererProps) {
    const [selectedNodeId, setSelectedNodeId] = useState<string | null>(payload.nodes[0]?.id ?? null);
    const [selectedEdgeId, setSelectedEdgeId] = useState<string | null>(null);
    const nodeById = useMemo(() => new Map(payload.nodes.map(node => [node.id, node])), [payload.nodes]);
    const selectedNode = selectedNodeId ? nodeById.get(selectedNodeId) : undefined;
    const selectedEdge = selectedEdgeId ? payload.edges.find(edge => edge.id === selectedEdgeId) : undefined;
    const { nodes, edges } = useMemo(() => toExplorerFlow(payload), [payload]);

    if (!nodes.length) {
        return <p className={styles.mutedText}>No visible graph items match the current filters.</p>;
    }

    return (
        <div className={styles.explorerGrid}>
            <div className={styles.flowCanvas} aria-label="Graph explorer">
                <ReactFlow
                    nodes={nodes}
                    edges={edges}
                    fitView
                    fitViewOptions={{ padding: 0.28 }}
                    minZoom={0.25}
                    maxZoom={1.8}
                    nodesConnectable={false}
                    onNodeClick={(_event, node) => {
                        setSelectedNodeId(node.id);
                        setSelectedEdgeId(null);
                    }}
                    onEdgeClick={(_event, edge) => {
                        setSelectedEdgeId(edge.id);
                        setSelectedNodeId(null);
                    }}
                >
                    <MiniMap pannable zoomable nodeColor={node => node.style?.background?.toString() ?? "#605e5c"} />
                    <Controls showInteractive={false} />
                    <Background color="#edebe9" gap={18} />
                </ReactFlow>
            </div>
            <aside className={styles.explorerDetails} aria-label="Selected graph item details">
                {selectedNode ? (
                    <NodeDetail node={selectedNode} onCitationClicked={onCitationClicked} />
                ) : selectedEdge ? (
                    <EdgeDetail edge={selectedEdge} nodeById={nodeById} />
                ) : (
                    <RelationshipList payload={payload} nodeById={nodeById} onCitationClicked={onCitationClicked} />
                )}
            </aside>
        </div>
    );
}

function toExplorerFlow(payload: GraphPayload) {
    const radius = Math.max(170, Math.min(390, payload.nodes.length * 18));
    const centerX = radius + 80;
    const centerY = radius + 30;
    const flowNodes: Node[] = payload.nodes.map((node, index) => {
        const angle = payload.nodes.length <= 1 ? 0 : (2 * Math.PI * index) / payload.nodes.length - Math.PI / 2;
        const origin = (node.source_origin ?? "context_graph") as GraphSourceOrigin;
        return {
            id: node.id,
            data: { label: node.label },
            position: {
                x: centerX + Math.cos(angle) * radius,
                y: centerY + Math.sin(angle) * radius
            },
            sourcePosition: undefined,
            targetPosition: undefined,
            style: {
                width: 150,
                minHeight: 46,
                borderRadius: 6,
                border: `2px solid ${SOURCE_COLORS[origin]}`,
                background: "#ffffff",
                color: "#242424",
                fontSize: 12,
                lineHeight: 1.25,
                padding: 8
            },
            ariaLabel: `${clinicalLabel(node)} ${node.label}`
        };
    });

    const flowEdges: Edge[] = payload.edges.map(edge => ({
        id: edge.id,
        source: edge.source,
        target: edge.target,
        label: safeEdgeLabel(edge),
        markerEnd: { type: MarkerType.ArrowClosed },
        style: { stroke: SOURCE_COLORS[(edge.source_origin ?? "context_graph") as GraphSourceOrigin], strokeWidth: 1.4 },
        labelStyle: { fill: "#605e5c", fontSize: 11 },
        labelBgStyle: { fill: "#ffffff", fillOpacity: 0.85 }
    }));

    return { nodes: flowNodes, edges: flowEdges };
}

function NodeDetail({ node, onCitationClicked }: { node: GraphNode; onCitationClicked?: (citationFilePath: string) => void }) {
    const date = getNodeProperty(node, "assessment_date");
    return (
        <div>
            <h3>{clinicalLabel(node)}</h3>
            <p className={styles.detailTitle}>{node.label}</p>
            <dl className={styles.detailList}>
                <dt>Source</dt>
                <dd>{sourceOriginLabel(node.source_origin)}</dd>
                {date != null && (
                    <>
                        <dt>Date</dt>
                        <dd>{String(date).slice(0, 10)}</dd>
                    </>
                )}
                {node.consumer_id && (
                    <>
                        <dt>Patient</dt>
                        <dd>{node.consumer_id}</dd>
                    </>
                )}
                {node.assessment_id && (
                    <>
                        <dt>Note</dt>
                        <dd>{node.assessment_id}</dd>
                    </>
                )}
            </dl>
            {node.assessment_id && (
                <button type="button" className={styles.openNoteButton} onClick={() => onCitationClicked?.(node.assessment_id!)}>
                    Open note
                </button>
            )}
        </div>
    );
}

function EdgeDetail({ edge, nodeById }: { edge: GraphEdge; nodeById: Map<string, GraphNode> }) {
    const source = nodeById.get(edge.source);
    const target = nodeById.get(edge.target);
    return (
        <div>
            <h3>Connection</h3>
            <p className={styles.detailTitle}>{safeEdgeLabel(edge)}</p>
            <dl className={styles.detailList}>
                <dt>Source</dt>
                <dd>{source?.label ?? edge.source}</dd>
                <dt>Target</dt>
                <dd>{target?.label ?? edge.target}</dd>
                <dt>Origin</dt>
                <dd>{sourceOriginLabel(edge.source_origin)}</dd>
            </dl>
        </div>
    );
}

function RelationshipList({
    payload,
    nodeById,
    onCitationClicked
}: {
    payload: GraphPayload;
    nodeById: Map<string, GraphNode>;
    onCitationClicked?: (citationFilePath: string) => void;
}) {
    return (
        <div className={styles.relationshipList}>
            {payload.edges.length ? (
                payload.edges.map(edge => (
                    <RelationshipRow key={edge.id} edge={edge} nodeById={nodeById} onCitationClicked={onCitationClicked} />
                ))
            ) : (
                <p className={styles.mutedText}>No visible connections match the current filters.</p>
            )}
        </div>
    );
}

function RelationshipRow({
    edge,
    nodeById,
    onCitationClicked
}: {
    edge: GraphEdge;
    nodeById: Map<string, GraphNode>;
    onCitationClicked?: (citationFilePath: string) => void;
}) {
    const source = nodeById.get(edge.source);
    const target = nodeById.get(edge.target);
    return (
        <div className={styles.relationshipRow}>
            <NodeButton node={source} fallback={edge.source} onCitationClicked={onCitationClicked} />
            <span className={styles.relationshipType}>{safeEdgeLabel(edge)}</span>
            <NodeButton node={target} fallback={edge.target} onCitationClicked={onCitationClicked} />
        </div>
    );
}

function NodeButton({
    node,
    fallback,
    onCitationClicked
}: {
    node?: GraphNode;
    fallback: string;
    onCitationClicked?: (citationFilePath: string) => void;
}) {
    if (!node) {
        return <span className={styles.nodeButton}>{fallback}</span>;
    }
    return (
        <button
            type="button"
            className={`${styles.nodeButton} ${node.assessment_id ? styles.nodeButtonClickable : ""}`}
            onClick={() => node.assessment_id && onCitationClicked?.(node.assessment_id)}
            disabled={!node.assessment_id}
        >
            {node.label}
        </button>
    );
}
